import { useState } from 'react';
import { COTE_MAX, enAdresseDeDonnees, reduitLImage } from '../../lib/image';
import { TAILLE_MAX_PIECE, enMo } from '../../lib/medias';
import { C } from '../../theme/palette';

/**
 * Photos annexées à une décharge : état des lieux, preuve d'un défaut existant.
 *
 * Même geste et même apparence que les photos d'un article (`ArticleMedia`),
 * mais un autre moment : la galerie d'un article envoie chaque fichier à
 * l'instant, parce que l'article existe déjà. La décharge, elle, n'existe
 * qu'une fois signée et enregistrée. Les photos attendent donc ici, réduites
 * comme celles d'un article, et partent au stockage avec la décharge — jamais
 * encodées dans la fiche.
 *
 * L'aperçu est une adresse `data:` gardée en mémoire le temps de la saisie :
 * c'est elle que l'aperçu A4 et l'impression affichent.
 *
 * @param {object} props propriétés du composant
 * @param {{cle: string, fichier: Blob, nom: string, apercu: string}[]} props.photos
 *   photos retenues, dans l'ordre d'ajout
 * @param {(photos: object[]) => void} props.onChange appelé avec la liste à jour
 * @param {boolean} [props.figees] vrai une fois la décharge enregistrée : les
 *   photos font alors partie de la pièce et ne se retouchent plus
 * @returns {import('react').ReactElement} la galerie et son bouton d'ajout
 */
export function PhotosDecharge({ photos, onChange, figees }) {
  const liste = photos || [];
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');

  const ajoute = async (evenement) => {
    const fichier = evenement.target.files && evenement.target.files[0];
    // Le champ est vidé aussitôt : sans cela, choisir deux fois le même
    // fichier ne déclencherait rien la seconde fois.
    evenement.target.value = '';
    if (!fichier) {
      return;
    }
    setErreur('');
    setEnvoi(true);
    try {
      const reduit = await reduitLImage(fichier, COTE_MAX);
      if (reduit.size > TAILLE_MAX_PIECE) {
        setErreur(
          `Cette photo pèse ${enMo(reduit.size)} Mo, au-delà des ${enMo(TAILLE_MAX_PIECE)} Mo admis.`,
        );
        return;
      }
      const apercu = await enAdresseDeDonnees(reduit);
      onChange([
        ...liste,
        {
          cle: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          fichier: reduit,
          nom: fichier.name,
          apercu,
        },
      ]);
    } catch (e) {
      setErreur(e?.message || 'La photo n’a pas pu être lue.');
    } finally {
      setEnvoi(false);
    }
  };

  const retire = (photo) => onChange(liste.filter((p) => p.cle !== photo.cle));

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
        {liste.map((p, i) => (
          <div key={p.cle} style={{ position: 'relative' }}>
            <img
              src={p.apercu}
              alt={`Photo ${i + 1} de la décharge`}
              style={{
                width: 72,
                height: 72,
                objectFit: 'cover',
                borderRadius: 9,
                border: '1px solid ' + C.border,
                display: 'block',
              }}
            />
            {!figees && (
              <button
                type="button"
                aria-label="Retirer la photo"
                onClick={() => retire(p)}
                style={{
                  position: 'absolute',
                  top: -7,
                  right: -7,
                  background: C.red,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 99,
                  width: 22,
                  height: 22,
                  fontSize: 13,
                  fontWeight: 800,
                  cursor: 'pointer',
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>
      {figees ? (
        <div style={{ fontSize: 11, color: C.muted, marginTop: 7, lineHeight: 1.5 }}>
          Décharge enregistrée : ses photos font partie de la pièce signée et ne se modifient plus.
        </div>
      ) : (
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 7,
            background: C.tealL,
            border: '1px solid ' + C.teal + '44',
            borderRadius: 9,
            padding: '9px 13px',
            fontSize: 10.5,
            fontWeight: 800,
            color: C.teal,
            cursor: envoi ? 'wait' : 'pointer',
            opacity: envoi ? 0.6 : 1,
          }}
        >
          📎 Ajouter une photo ou un plan{' '}
          <span style={{ fontWeight: 600, fontSize: 8.5, opacity: 0.75 }}>facultatif</span>
          <input
            type="file"
            accept="image/*"
            disabled={envoi}
            style={{ display: 'none' }}
            onChange={ajoute}
          />
        </label>
      )}
      {erreur && (
        <div
          role="alert"
          style={{
            marginTop: 6,
            fontSize: 11,
            color: C.red,
            background: C.red + '15',
            borderRadius: 7,
            padding: '6px 9px',
            lineHeight: 1.5,
          }}
        >
          {erreur}
        </div>
      )}
    </div>
  );
}
